/**
 * Proyectos — la cuarta sección del Editor Studio (11-sep-2026).
 *
 * Hasta hoy solo había UN libro, y era la ruta que este navegador recordaba. Quien escribe dos
 * cosas a la vez tenía que buscar la carpeta con el ratón cada vez que cambiaba, y quien vaciaba
 * los datos del sitio se quedaba sin nada elegido sin saber por qué.
 *
 * Un proyecto es un nombre y una carpeta. Dos formas de tenerlo, y las dos importan:
 *
 *  · EMPEZAR UNO NUEVO, y el motor crea la carpeta en `~/HumanInk/Proyectos/`;
 *  · TRAER UNO QUE YA EXISTE, eligiendo su carpeta. No se mueve ni un fichero, y lo que ya hubiera
 *    dentro —bitácora, versiones, certificados— sigue sirviendo. Este es el caso de cualquiera que
 *    llegue con un libro empezado, que son todos.
 *
 * Y lo que esta pantalla dice en voz alta: **quitar un proyecto de la lista no borra nada**. Es la
 * única frase de aquí que, si fuera mentira, no se podría deshacer.
 */
import { useCallback, useEffect, useState } from "react";
import { GENEROS_DE_LIBRO, TIPOS_DE_OBRA, adoptarProyecto, capitulosDeTexto, clasificarProyecto, documento, guardarCortes, autores as listarAutores, crearProyecto, elegirDelSistema, elegirProyecto, olvidarProyecto, proponerGenero, proponerTipo, proyecto as leerCarpeta, proyectos, renombrarProyecto, subir, vincularProyecto } from "../api";
import { carpetaNoValeParaLibro, dentroDeCarpeta, generoEnPalabras, generoGuardado, guardarOtro, necesitaGenero, nombreDeRuta, tituloDeCapitulo } from "../logica";
import { avisoSinEstilo, capitulosReales, nombreConVersion, versionDelNombre } from "../logica";
import type { Autor, Proyecto } from "../api";
import LibrosEnCarpeta from "./LibrosEnCarpeta";

const dia = (iso: string) => {
  try {
    return new Date(iso).toLocaleDateString("es-ES", { day: "numeric", month: "short", year: "numeric" });
  } catch {
    return "—";
  }
};

export default function Proyectos({ carpeta, doc, alElegir, alDoc }: {
  carpeta: string;
  doc: string | null;
  alElegir: (dir: string) => void;
  /** El manuscrito señalado dentro del proyecto abierto. */
  alDoc: (ruta: string) => void;
}) {
  const [lista, setLista] = useState<Proyecto[] | null>(null);
  const [activo, setActivo] = useState<string | null>(null);
  const [nombre, setNombre] = useState("");
  const [suya, setSuya] = useState<string | null>(null);   // carpeta elegida a mano, si la hay
  const [fallo, setFallo] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  const [confirmar, setConfirmar] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);
  // Las firmas, para el selector de cada libro. Un motor anterior no las tiene: el selector no sale.
  const [firmas, setFirmas] = useState<Autor[] | null>(null);
  const [nuevoNombre, setNuevoNombre] = useState("");
  const [subiendo, setSubiendo] = useState(false);
  // R-06: si la carpeta del proyecto abierto es Descargas o similar, se dice y se ofrece pasarlo a una propia.
  const [motivoCarpeta, setMotivoCarpeta] = useState<string | null>(null);
  const [nombreNuevo, setNombreNuevo] = useState("");
  const cuantos = async (ruta: string) => {
    try { const d = await leerCarpeta(ruta); return "files" in d ? d.files.length : undefined; } catch { return undefined; }
  };

  const mirar = useCallback(async () => {
    const d = await proyectos();
    setLista(d.proyectos);
    setActivo(d.activo);
    return d;
  }, []);

  useEffect(() => { listarAutores().then((d) => setFirmas(d.autores)).catch(() => setFirmas(null)); }, []);

  // PROYECTOS II · qué firma lleva el libro. Vacío deshace el vínculo.
  async function firmar(p: Proyecto, autor: string) {
    setFallo(null);
    try {
      await vincularProyecto(p.id, { autor: autor || null });
      await mirar();
    } catch (e) {
      setFallo((e as Error).message);
    }
  }

  useEffect(() => {
    (async () => {
      try {
        const d = await mirar();
        // LA MIGRACIÓN. Quien ya tenía una carpeta elegida en este navegador no debe estrenar esta
        // pantalla y encontrarla vacía: se adopta como proyecto, una sola vez, y queda activo.
        if (!d.proyectos.length && carpeta) {
          const { proyecto } = await adoptarProyecto(carpeta);
          // `adoptado` distingue «lo he traído ahora» de «ya estaba»: sin mirarlo, la pantalla
          // anunciaría que ha traído un libro en casos en que no ha hecho nada.
          if (proyecto?.adoptado) {
            setAviso(`He traído «${proyecto.nombre}» de la carpeta que tenías elegida. No se ha movido ningún fichero.`);
            await mirar();
          }
        }
      } catch (e) {
        setFallo(/404/.test(String((e as Error)?.message))
          ? "El HumanInk de este ordenador es anterior a esta pantalla. Actualízalo desde Ajustes y vuelve."
          : (e as Error).message);
      }
    })();
  }, [mirar, carpeta]);

  // 21-sep (Rais: «le doy a Empezar y no hace nada»). Sí hacía: creaba el proyecto y lo decía ARRIBA
  // del todo, y el botón está al final de la página. El resultado se dice también aquí, al lado.
  const [resultadoNuevo, setResultadoNuevo] = useState<{ ok: boolean; texto: string } | null>(null);
  async function nuevo() {
    if (!nombre.trim()) return;
    setOcupado(true); setFallo(null); setAviso(null); setResultadoNuevo(null);
    try {
      const p = await crearProyecto({ nombre: nombre.trim(), carpeta: suya ?? undefined });
      setNombre(""); setSuya(null);
      const dicho = p.creada
        ? `Creado. Su carpeta es ${p.carpeta} y ahí caerán sus ficheros. Ya trabajas en él.`
        : `Traído. Sigue en ${p.carpeta} y no se ha movido nada. Ya trabajas en él.`;
      setAviso(dicho);
      setResultadoNuevo({ ok: true, texto: dicho });
      await mirar();
      alElegir(p.carpeta);
    } catch (e) {
      setFallo((e as Error).message);
      setResultadoNuevo({ ok: false, texto: (e as Error).message });
    } finally {
      setOcupado(false);
    }
  }

  async function buscarCarpeta() {
    setFallo(null);
    try {
      const { ruta, cancelado } = await elegirDelSistema("carpeta", suya ?? undefined);
      if (cancelado || !ruta) return;
      const motivo = carpetaNoValeParaLibro(ruta, await cuantos(ruta));
      if (motivo) {
        setFallo(`${motivo} Empieza un proyecto nuevo con el nombre del libro y sube ahí tu manuscrito: se copia y el original no se toca.`);
        return;
      }
      setSuya(ruta);
      if (!nombre.trim()) setNombre(ruta.split(/[/\\]/).filter(Boolean).pop() ?? "");
    } catch (e) {
      setFallo((e as Error).message);
    }
  }

  async function abrir(p: Proyecto) {
    setFallo(null); setAviso(null);
    try {
      await elegirProyecto(p.id);
      setActivo(p.id);
      alElegir(p.carpeta);
      await mirar();
    } catch (e) {
      setFallo((e as Error).message);
    }
  }

  async function olvidar(p: Proyecto) {
    setFallo(null);
    try {
      const eraElAbierto = p.id === activo;
      const r = await olvidarProyecto(p.id);
      setConfirmar(null);
      setAviso(`«${p.nombre}» ya no está en la lista. Sus ficheros siguen en ${r.carpeta}: no se ha borrado nada.`);
      const d = await mirar();
      // Si era el que estaba abierto, el Studio se quedaba trabajando sobre un libro que ya no
      // sale en la lista: la cabecera con su nombre y el chat con su carpeta. Se abre el que quede.
      if (eraElAbierto) {
        const siguiente = d.proyectos.find((x) => x.id === d.activo && x.existe);
        if (siguiente) alElegir(siguiente.carpeta);
      }
    } catch (e) {
      setFallo((e as Error).message);
    }
  }

  const abierto = lista?.find((p) => p.id === activo) ?? null;
  useEffect(() => {
    if (!abierto?.existe) { setMotivoCarpeta(null); return; }
    let vigente = true;
    cuantos(abierto.carpeta).then((n) => { if (vigente) setMotivoCarpeta(carpetaNoValeParaLibro(abierto.carpeta, n)); });
    setNombreNuevo(doc ? nombreDeRuta(doc).replace(/\.[^.]+$/, "") : "");
    return () => { vigente = false; };
  }, [abierto?.id, abierto?.carpeta, abierto?.existe]);

  // Pasar el libro a una carpeta propia: proyecto nuevo en ~/HumanInk/Proyectos y el manuscrito
  // copiado dentro. Nada se mueve ni se borra de la carpeta de antes.
  async function pasarACarpetaPropia(f: File | undefined) {
    if (!f || !nombreNuevo.trim()) return;
    setSubiendo(true); setFallo(null); setAviso(null);
    try {
      const p = await crearProyecto({ nombre: nombreNuevo.trim() });
      alElegir(p.carpeta);
      const conVersion = nombreConVersion(f.name);
      const r = await subir(p.carpeta, f, conVersion ?? undefined);
      alDoc(r.path);
      setAviso(`Listo: «${p.nombre}» tiene su carpeta (${p.carpeta}) y tu manuscrito está dentro${conVersion ? `, como «${r.name}»: le hemos puesto el número de versión en el nombre (${versionDelNombre(r.name)}) para que cada pasada cree la siguiente` : ""}. Tu original no se ha tocado.`);
      await mirar();
    } catch (e) {
      setFallo((e as Error).message);
    } finally {
      setSubiendo(false);
    }
  }
  const firmaDe = (p: Proyecto | null) => (p?.autor ? firmas?.find((a) => a.id === p.autor)?.nombre ?? "una firma que ya no está en la lista" : null);
  const otros = (lista ?? []).filter((p) => p.id !== activo);
  useEffect(() => { setNuevoNombre(abierto?.nombre ?? ""); }, [abierto?.id, abierto?.nombre]);

  async function renombrar() {
    if (!abierto || !nuevoNombre.trim() || nuevoNombre.trim() === abierto.nombre) return;
    setFallo(null); setAviso(null);
    try {
      await renombrarProyecto(abierto.id, { nombre: nuevoNombre.trim() });
      setAviso("Nombre cambiado. La carpeta no se renombra: sigue donde estaba.");
      await mirar();
    } catch (e) {
      setFallo((e as Error).message);
    }
  }

  // Elegir otro manuscrito de los que ya están en la carpeta del proyecto. Uno de fuera no se
  // señala sin más: el proyecto trabajaría sobre un fichero que no es suyo. Para eso está «Subir»,
  // que lo copia dentro sin tocar el original.
  async function elegirManuscrito() {
    if (!abierto) return;
    setFallo(null); setAviso(null);
    try {
      const { ruta, cancelado } = await elegirDelSistema("documento", abierto.carpeta);
      if (cancelado || !ruta) return;
      if (!dentroDeCarpeta(ruta, abierto.carpeta)) {
        setFallo(`Ese fichero está fuera de la carpeta del proyecto. Usa «Subir un manuscrito» y se copiará dentro; el original no se toca.`);
        return;
      }
      alDoc(ruta);
      setAviso(`Ahora trabajas sobre «${nombreDeRuta(ruta)}».`);
    } catch (e) {
      setFallo((e as Error).message);
    }
  }

  async function subirManuscrito(f: File | undefined) {
    if (!abierto || !f) return;
    setSubiendo(true); setFallo(null); setAviso(null);
    try {
      const conVersion = nombreConVersion(f.name);
      const r = await subir(abierto.carpeta, f, conVersion ?? undefined);
      alDoc(r.path);
      // Rais, 21-sep: «un aviso: versionamos el nombre del fichero». Si se le ha puesto versión al
      // nombre, se dice con esas palabras; el original, fuera de la carpeta, no se toca.
      const version = conVersion ? ` Le hemos puesto el número de versión en el nombre («${f.name}» → «${r.name}», ${versionDelNombre(r.name)}): así cada pasada crea la siguiente. Tu original no se toca.` : "";
      setAviso(r.igual
        ? `«${r.name}» ya estaba en la carpeta: no se ha duplicado. Es tu manuscrito ahora.`
        : r.renombrado
          ? `Ya había un «${r.pedido}» distinto: el nuevo se ha guardado como «${r.name}» y el anterior sigue intacto.${version}`
          : `Subido a la carpeta del proyecto. Ahora trabajas sobre «${r.name}».${version}`);
    } catch (e) {
      setFallo((e as Error).message);
    } finally {
      setSubiendo(false);
    }
  }

  return (
    <>
      {fallo && <section className="carta"><p className="vacio" style={{ color: "var(--acc)" }}>{fallo}</p></section>}
      {aviso && <section className="carta"><p className="vacio">{aviso}</p></section>}

      {/* 17-sep (Rais): «la pantalla de proyectos es muy confusa». Primero, una ficha con lo que
          tienes ahora —manuscrito, proyecto, autor—; aparte, cómo cambiar cada cosa. */}
      <section className="carta">
        <h2>Lo que tienes abierto</h2>
        {lista === null ? <p className="vacio">…</p> : !abierto ? (
          <p className="vacio">
            Todavía no hay ningún proyecto abierto. Empieza uno nuevo aquí abajo, o trae el libro que
            ya tengas empezado eligiendo su carpeta: no se moverá de donde está.
          </p>
        ) : (
          <dl className="ficha-actual">
            <div>
              <dt>Manuscrito</dt>
              <dd>{doc ? <b>{nombreDeRuta(doc)}</b> : <span className="vacio">sin elegir</span>}</dd>
            </div>
            <div>
              <dt>Proyecto</dt>
              <dd>
                <b>{abierto.nombre}</b>
                {!abierto.existe && <span className="grado alta" style={{ marginLeft: 8 }}>la carpeta ya no está</span>}
                {abierto.descripcion && <span className="vacio" style={{ display: "block" }}>{abierto.descripcion}</span>}
              </dd>
            </div>
            <div>
              <dt>Qué es</dt>
              <dd>
                {abierto.tipo
                  ? <b>{TIPOS_DE_OBRA.find((t) => t.id === abierto.tipo)?.nombre ?? abierto.tipo}</b>
                  : <span className="grado alta">sin decir · el informe no se lanza</span>}
                {generoGuardado(abierto.genero, GENEROS_DE_LIBRO, abierto.genero_leido)
                  ? <span className="vacio" style={{ display: "block" }}>{generoEnPalabras(generoGuardado(abierto.genero, GENEROS_DE_LIBRO, abierto.genero_leido))}</span>
                  : necesitaGenero(abierto.tipo) && <span className="grado alta" style={{ display: "inline-block", marginTop: 4 }}>género sin decir · el informe no se lanza</span>}
              </dd>
            </div>
            <div>
              <dt>Autor</dt>
              <dd>{firmaDe(abierto) ? <b>{firmaDe(abierto)}</b> : <span className="vacio">sin firma</span>}</dd>
            </div>
            <div>
              <dt>Carpeta</dt>
              <dd className="vacio mono">{abierto.carpeta}</dd>
            </div>
          </dl>
        )}
      </section>

      {/* R0-7: dos libros en la carpeta del proyecto. Si la carpeta es Descargas o similar ya lo dice
          el aviso de abajo, que pide una carpeta propia: no se dicen las dos cosas a la vez. */}
      {abierto && !motivoCarpeta && abierto.existe && (
        <LibrosEnCarpeta carpeta={abierto.carpeta} doc={doc} alSeparado={() => { mirar().catch(() => {}); }}
          alAbrir={async (x) => { const d = await mirar(); const p = d.proyectos.find((y) => y.id === x.id); if (p) await abrir(p); }} />
      )}

      {abierto && motivoCarpeta && (
        <section className="carta">
          <h2>Tu libro necesita su propia carpeta</h2>
          <p className="vacio">
            {motivoCarpeta} Con tantos documentos alrededor, el Studio puede proponer el que no es y los
            entregables se mezclan con todo lo demás.
          </p>
          <div className="formulario">
            <label className="campo">
              <span>Nombre del libro</span>
              <div className="dir">
                <input className="escribir" value={nombreNuevo} onChange={(e) => setNombreNuevo(e.target.value)} placeholder="El nombre del libro" />
              </div>
            </label>
            <div className="pie-form">
              <p className="vacio">Se crea <b className="mono">~/HumanInk/Proyectos/{nombreNuevo.trim() || "…"}</b> y se copia ahí el manuscrito que elijas.</p>
              <label className="btn" style={{ cursor: subiendo || !nombreNuevo.trim() ? "not-allowed" : "pointer", opacity: nombreNuevo.trim() ? 1 : 0.45 }}>
                {subiendo ? "Copiando…" : "Elegir el manuscrito y pasarlo"}
                <input type="file" hidden accept=".docx,.md,.txt" disabled={subiendo || !nombreNuevo.trim()}
                       onChange={(e) => { pasarACarpetaPropia(e.target.files?.[0]); e.target.value = ""; }} />
              </label>
            </div>
          </div>
        </section>
      )}

      {abierto && abierto.existe && (
        <section className="carta">
          <h2>Qué es tu libro<span className="sub">decide con qué vara se lee</span></h2>
          <QueEs p={abierto} doc={doc} alCambio={(x) => setLista((l) => (l ? l.map((y) => (y.id === x.id ? x : y)) : l))} />
        </section>
      )}

      {abierto && doc && (
        <section className="carta">
          <h2>Los capítulos<span className="sub">cómo se ha partido tu libro</span></h2>
          <Cortes carpeta={abierto.carpeta} doc={doc} />
        </section>
      )}

      {abierto && (
        <section className="carta">
          <h2>Cambiar</h2>
          <div className="formulario">
            <div className="campo">
              <span>Manuscrito</span>
              <div className="botones">
                <button className="btn fantasma" onClick={elegirManuscrito}>Elegir otro de la carpeta</button>
                <label className="btn fantasma" style={{ cursor: subiendo ? "wait" : "pointer" }}>
                  {subiendo ? "Subiendo…" : "Subir un manuscrito"}
                  <input type="file" hidden accept=".docx,.md,.txt" disabled={subiendo}
                         onChange={(e) => { subirManuscrito(e.target.files?.[0]); e.target.value = ""; }} />
                </label>
              </div>
            </div>

            <label className="campo">
              <span>Nombre del proyecto</span>
              <div className="dir">
                <input className="escribir" value={nuevoNombre} onChange={(e) => setNuevoNombre(e.target.value)}
                       onKeyDown={(e) => { if (e.key === "Enter") renombrar(); }} />
              </div>
            </label>
            {nuevoNombre.trim() && nuevoNombre.trim() !== abierto.nombre && (
              <div className="pie-form">
                <p className="vacio">Solo cambia el nombre; la carpeta sigue donde está.</p>
                <button className="btn" onClick={renombrar}>Guardar el nombre</button>
              </div>
            )}

            <label className="campo">
              <span>Autor</span>
              {firmas === null ? <p className="vacio">Este HumanInk todavía no tiene firmas.</p>
                : firmas.length ? (
                  <select className="escribir" value={abierto.autor ?? ""} onChange={(e) => firmar(abierto, e.target.value)}>
                    <option value="">— sin firma —</option>
                    {firmas.map((a) => <option key={a.id} value={a.id}>{a.nombre}</option>)}
                    {abierto.autor && !firmas.some((a) => a.id === abierto.autor) && <option value={abierto.autor}>(una firma que ya no está en la lista)</option>}
                  </select>
                ) : <p className="vacio">Todavía no hay firmas: se crean en Autores.</p>}
            </label>
          </div>
        </section>
      )}

      {!!otros.length && (
        <section className="carta">
          <h2>Tus otros proyectos<span className="sub">{otros.length === 1 ? "1 libro" : `${otros.length} libros`}</span></h2>
          <div className="issues">
            {otros.map((p) => (
              <div className="issue fila-proyecto" key={p.id}>
                <div className="que">
                  <p>
                    <b>{p.nombre}</b>
                    {!p.existe && (
                      <span className="grado alta" style={{ marginLeft: 8 }}>
                        {p.estado === "no_esta" ? "la carpeta ya no está" : "eso ya no es una carpeta"}
                      </span>
                    )}
                  </p>
                  <p className="vacio">
                    {firmaDe(p) ? `${firmaDe(p)} · ` : ""}
                    {p.existe ? `abierto por última vez el ${dia(p.visto)}` : "puede que la hayas movido o que viviera en un disco que no está conectado"}
                  </p>
                  {p.descripcion && <p className="vacio">{p.descripcion}</p>}
                  <p className="vacio mono">{p.carpeta}</p>
                </div>
                <div className="acciones">
                  {confirmar === p.id ? (
                    <>
                      {/* El peso va en el seguro. Con el destructivo en primario, lo que la
                          pantalla invita a pulsar es justo lo que hay que pensar dos veces. */}
                      <button className="btn" onClick={() => setConfirmar(null)}>No, dejarlo</button>
                      <button className="btn fantasma" onClick={() => olvidar(p)}>Sí, quitar de la lista</button>
                    </>
                  ) : (
                    <>
                      <button className="btn texto" onClick={() => { setConfirmar(p.id); setAviso(null); }}>Quitar</button>
                      {p.existe && <button className="btn fantasma" onClick={() => abrir(p)}>Abrir</button>}
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
          {/* La frase que evita el susto: qué hace «Quitar» y qué no, ANTES de que nadie lo pulse. */}
          <p className="vacio">
            Quitar un proyecto solo lo saca de esta lista. <b>No borra ni mueve ningún fichero</b>.
          </p>
        </section>
      )}

      <section className="carta">
        <h2>Empezar o traer otro libro<span className="sub">un nombre y una carpeta</span></h2>

        {/* Misma medida que «Nueva firma»: el campo ceñido y la acción debajo, con lo que va a pasar. */}
        <div className="formulario">
          <label className="campo">
            <span>Nombre del libro</span>
            <div className="dir">
              <input
                className="escribir" placeholder="El nombre del libro"
                value={nombre} onChange={(e) => setNombre(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") nuevo(); }}
              />
            </div>
          </label>
          {/* Si la carpeta elegida no se llama como el proyecto, se dice ANTES de crearlo: así nació
              un «Donde el mañana viene a morir» cuya carpeta era «hacking-innovation-1-v30-2», y
              después nadie sabía cuál era cuál (Rais, 23-sep). */}
          {suya && (
            <p className="vacio">
              Carpeta: <b className="mono">{suya}</b>
              {nombre.trim() && nombreDeRuta(suya).toLowerCase() !== nombre.trim().toLowerCase().replace(/\s+/g, "-") && (
                <> · se seguirá llamando <b>{nombreDeRuta(suya)}</b> en el disco, aunque el proyecto se llame «{nombre.trim()}»: dentro hay un libro con su historia y renombrarla rompería las rutas.</>
              )}
            </p>
          )}
          <div className="pie-form">
            <p className="vacio">
              {suya
                ? <>Se usará la carpeta que has elegido y <b>no se moverá nada</b> de dentro.</>
                : <>Se creará una carpeta con ese nombre en <b className="mono">~/HumanInk/Proyectos/</b>.</>}
            </p>
            <button className="btn" onClick={nuevo} disabled={!nombre.trim() || ocupado}>
              {ocupado ? "Un momento…" : suya ? "Traer este libro" : "Empezar"}
            </button>
          </div>
          {resultadoNuevo && (
            <p className="vacio" role="status" style={resultadoNuevo.ok ? undefined : { color: "var(--acc)" }}>{resultadoNuevo.texto}</p>
          )}
          <div className="pie-form">
            <button className="btn fantasma" onClick={buscarCarpeta}>
              {suya ? "Elegir otra carpeta" : "Ya tengo el libro: elegir su carpeta"}
            </button>
            {suya && <button className="btn texto" onClick={() => setSuya(null)}>Mejor una nueva</button>}
          </div>
        </div>
      </section>
    </>
  );
}

/**
 * Qué es el libro (M-01). Seis tipos, lista cerrada, y el motor propone leyendo el principio del
 * documento: propone, no decide. Sin tipo el informe no se lanza, porque juzgar un ensayo con vara
 * de novela da un informe que miente (el de Hacking Innovation sacó un 1 en personajes).
 */
export function QueEs({ p, doc, alCambio }: { p: Proyecto; doc: string | null; alCambio: (p: Proyecto) => void }) {
  const [ocupado, setOcupado] = useState(false);
  const [propuesto, setPropuesto] = useState<string | null>(null);
  const [generoPropuesto, setGeneroPropuesto] = useState<string | null>(null);
  const [fallo, setFallo] = useState<string | null>(null);
  const guardado = generoGuardado(p.genero, GENEROS_DE_LIBRO, p.genero_leido);
  const [otro, setOtro] = useState(guardado?.id === "otro" ? guardado.descripcion ?? "" : "");
  const [eligeOtro, setEligeOtro] = useState(guardado?.id === "otro");

  async function poner(tipo: (typeof TIPOS_DE_OBRA)[number]["id"]) {
    setOcupado(true); setFallo(null);
    try { alCambio(await clasificarProyecto(p.id, { tipo })); } catch (e) { setFallo((e as Error).message); } finally { setOcupado(false); }
  }
  async function ponerGenero(genero: string) {
    setOcupado(true); setFallo(null);
    try { alCambio(await clasificarProyecto(p.id, { genero })); } catch (e) { setFallo((e as Error).message); } finally { setOcupado(false); }
  }
  async function proponer() {
    if (!doc) return;
    setOcupado(true); setFallo(null); setPropuesto(null); setGeneroPropuesto(null);
    try {
      const r = await proponerTipo(doc);
      if (!r.tipo) { setFallo("El motor no ha sabido decirlo con el principio del libro: elígelo tú."); return; }
      setPropuesto(r.tipo); await poner(r.tipo);
      // En ficción también el género (27-sep): propone y dice si no encaja en ninguno; decide el autor.
      if (necesitaGenero(r.tipo)) {
        const g = await proponerGenero(doc).catch(() => null);
        if (g?.genero) {
          const valor = g.genero.id === "otro" ? (g.genero.descripcion ?? "") : g.genero.id;
          if (valor) {
            setGeneroPropuesto(g.genero.id === "otro" ? `otro: «${valor}»` : GENEROS_DE_LIBRO.find((x) => x.id === valor)?.nombre ?? valor);
            if (g.genero.id === "otro") { setEligeOtro(true); setOtro(valor); }
            await ponerGenero(g.genero.id === "otro" ? guardarOtro(valor) : valor);
          }
        }
      }
    } catch (e) { setFallo((e as Error).message); } finally { setOcupado(false); }
  }
  const elegido = TIPOS_DE_OBRA.find((t) => t.id === p.tipo);
  const pideGenero = necesitaGenero(p.tipo);
  return (
    <div className="formulario" style={{ maxWidth: "none" }}>
      <div className="adjuntos">
        {TIPOS_DE_OBRA.map((t) => (
          <button key={t.id} className={"chip" + (p.tipo === t.id ? " puesto" : "")} disabled={ocupado} onClick={() => poner(t.id)} title={t.mira}>
            {p.tipo === t.id ? "✓ " : ""}{t.nombre}
          </button>
        ))}
      </div>
      {elegido && <p className="vacio">Con {elegido.nombre.toLowerCase()} se mira: {elegido.mira}. La exigencia es la misma para todos los libros: el tipo cambia qué se mira, no cuánto se pide.</p>}
      {propuesto && <p className="vacio">El motor ha leído el documento y propone <b>{TIPOS_DE_OBRA.find((t) => t.id === propuesto)?.nombre}</b>{generoPropuesto ? <>, de género <b>{generoPropuesto}</b></> : null}. Cámbialo si no es eso.</p>}
      {pideGenero && (
        <>
          <p className="vacio"><b>Género</b> — con él se elige cómo se lee la estructura: una novela literaria no se mide con la vara de un thriller.</p>
          <div className="adjuntos">
            {GENEROS_DE_LIBRO.map((g) => {
              const puesto = g.id === "otro" ? eligeOtro : guardado?.id === g.id;
              return (
                <button key={g.id} className={"chip" + (puesto ? " puesto" : "")} disabled={ocupado}
                        onClick={() => { if (g.id === "otro") { setEligeOtro(true); } else { setEligeOtro(false); ponerGenero(g.id); } }}>
                  {puesto ? "✓ " : ""}{g.nombre}
                </button>
              );
            })}
          </div>
          {eligeOtro && (
            <label className="campo" style={{ margin: 0 }}>
              <span>Descríbelo con tus palabras</span>
              <div className="dir">
                <input className="escribir" value={otro} placeholder="p. ej. fábula filosófica coral"
                       onChange={(e) => setOtro(e.target.value)}
                       onBlur={() => { if (otro.trim() && (guardado?.id !== "otro" || otro.trim() !== (guardado?.descripcion ?? ""))) ponerGenero(guardarOtro(otro)); }} />
              </div>
            </label>
          )}
          {guardado && !eligeOtro && guardado.lente && <p className="vacio">Se leerá con la lente «{guardado.lente}».</p>}
          {guardado?.a_mano && guardado.id !== "otro" && (
            <p className="vacio">Lo escribiste como «{guardado.descripcion}» y se lee como {guardado.nombre.toLowerCase()}. Si no es eso, elige otro o «Otro» y descríbelo.</p>
          )}
          {eligeOtro && <p className="vacio">Con «Otro» no se aplica la vara de ningún género de mercado: el lector dice al principio con qué lente lo lee, a partir de tu descripción.</p>}
          {!guardado && <p className="vacio" style={{ color: "var(--acc)" }}>Sin género, el informe de lectura no se lanza.</p>}
        </>
      )}
      <div className="botones">
        <button className="btn fantasma" disabled={!doc || ocupado} onClick={proponer}>
          {ocupado ? "Leyendo el principio…" : "Que lo proponga el motor"}
        </button>
      </div>
      {fallo && <p className="vacio" style={{ color: "var(--acc)" }}>{fallo}</p>}
    </div>
  );
}

/**
 * Revisar los cortes de capítulo (M-03). Se enseñan tal como el motor los ve —de los estilos del
 * documento o del texto— y el autor puede quitar los que no son capítulos. Lo que guarda manda a
 * partir de entonces: el mapa del informe, el alcance de un encargo y las pasadas leen eso.
 */
export function Cortes({ carpeta, doc }: { carpeta: string; doc: string }) {
  const [caps, setCaps] = useState<{ title: string; palabras: number }[] | null>(null);
  const [cortes, setCortes] = useState<{ i: number; titulo: string }[] | null>(null);
  const [fuente, setFuente] = useState<string | null>(null);
  const [fiable, setFiable] = useState(true);
  const [motivo, setMotivo] = useState<string | null>(null);
  const [sinEstilo, setSinEstilo] = useState<{ texto: string }[]>([]);
  const [ocupado, setOcupado] = useState(false);
  const [guardado, setGuardado] = useState(false);
  const [fallo, setFallo] = useState<string | null>(null);

  useEffect(() => {
    let vivo = true;
    setCaps(null); setCortes(null); setGuardado(false); setFallo(null);
    documento(doc).then(async (d) => {
      if (!vivo || "error" in d) return;
      const r = await capitulosDeTexto(d.text, carpeta, doc);
      if (!vivo) return;
      setCaps(r.capitulos.map((c) => ({ title: c.title, palabras: c.palabras })));
      setCortes(r.cortes ?? null);
      setFuente(r.fuente ?? null);
      setFiable(r.fiable !== false);
      setMotivo(r.motivo ?? null);
      setSinEstilo(r.sin_estilo ?? []);
    }).catch(() => { if (vivo) setFallo("No he podido leer el documento."); });
    return () => { vivo = false; };
  }, [carpeta, doc]);

  async function quitar(i: number) {
    if (!cortes) return;
    const quedan = cortes.filter((c) => c.i !== i);
    if (quedan.length < 2) { setFallo("Tienen que quedar al menos dos capítulos."); return; }
    setOcupado(true); setFallo(null);
    try {
      await guardarCortes(carpeta, doc, quedan);
      setCortes(quedan); setGuardado(true); setFuente("autor");
      const d = await documento(doc);
      if (!("error" in d)) {
        const r = await capitulosDeTexto(d.text, carpeta, doc);
        setCaps(r.capitulos.map((c) => ({ title: c.title, palabras: c.palabras })));
      }
    } catch (e) { setFallo((e as Error).message); } finally { setOcupado(false); }
  }

  if (caps === null) return <p className="vacio">Leyendo cómo está partido tu libro…</p>;
  const deDonde = fuente === "autor" ? "los cortes que corregiste"
    : fuente === "estilos" ? "los estilos de título del documento"
    : "el texto, porque el documento no trae estilos de título";
  const reales = capitulosReales(caps).length;
  const hayPreliminares = reales < caps.length;
  const aviso = avisoSinEstilo(sinEstilo);
  let n = 0;
  return (
    <>
      <p className="vacio">
        {reales} {reales === 1 ? "capítulo" : "capítulos"}{hayPreliminares ? " y lo que va antes del primero" : ""}, según {deDonde}.
        {!fiable && motivo ? ` No es de fiar: ${motivo}.` : ""}
        {guardado ? " Guardado: a partir de ahora manda tu versión." : ""}
      </p>
      {aviso && <p className="vacio" style={{ color: "var(--acc)" }}>{aviso}</p>}
      <div className="mapa">
        {caps.map((c, k) => {
          const corte = cortes?.find((x) => x.titulo === c.title);
          return (
            <div className="cap" key={k}>
              <span className="idx mono">{c.title === "Preliminares" ? "·" : ++n}</span>
              <span className="tit">{tituloDeCapitulo(c.title)}</span>
              <b className="mono">{c.palabras.toLocaleString("es-ES")}</b>
              {corte && cortes && cortes.length > 2 && (
                <button className="chip" disabled={ocupado} title="Esto no es un capítulo: júntalo con el anterior"
                        onClick={() => quitar(corte.i)}>No es capítulo</button>
              )}
            </div>
          );
        })}
      </div>
      {!cortes && <p className="vacio">Para poder corregir los cortes hace falta el motor 1.6.7 y un .docx con estilos de título.</p>}
      {fallo && <p className="vacio" style={{ color: "var(--acc)" }}>{fallo}</p>}
    </>
  );
}
